import React, {Suspense, lazy, useState} from 'react';
import type {Area} from 'react-easy-crop';
import {useTranslation} from 'react-i18next';
import {ANALYTIC_EVENTS, Analytics} from '../../../analytics';
import {compressImage, MainLoader} from '@chekin/guestapp-ui-kit';
import {getFileSizeMB, IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB} from '@chekin/core';
import notIncludedIcon from '../../../assets/icons/not-included-icon.svg?no-inline';
import checkIcon from '../../../assets/icons/check.svg?no-inline';
import {IncludedRow, StyledUploadPhoto, Photo} from './styled';

const ImageCropper = lazy(() =>
  import('./ImageCropper').then(({ImageCropper}) => ({default: ImageCropper})),
);

const CropperLoader = () => (
  <div className="w-full max-w-[420px]">
    <div className="relative flex aspect-[410/262] w-full items-center justify-center overflow-hidden rounded-xl bg-white lg:h-[262px] lg:w-[410px]">
      <MainLoader visible width={56} />
    </div>
  </div>
);

function getAllowedFormats() {
  return {
    regExpFormats: '.jpg|.jpeg|.png',
    errorMessage: 'incorrect_file_type_only_file_types_allowed',
  };
}

type UploadPhotoProps = {
  image: string | null;
  croppedImage: string | null;
  isCropping: boolean;
  onUpload: (screenshot: string) => void;
  onDisabled: (value: boolean) => void;
  onCropComplete: (croppedAreaPixels: Area) => void;
};

const UploadPhoto = React.memo(
  ({
    image,
    croppedImage,
    isCropping,
    onUpload,
    onDisabled,
    onCropComplete,
  }: UploadPhotoProps) => {
    const {t} = useTranslation();
    const [validationError, setValidationError] = useState<string | null>();
    const ref = React.useRef<HTMLInputElement>(null);

    const handleImage = (imageFile?: File) => {
      if (!imageFile) return;
      compressImage(imageFile)
        .then(onUpload)
        .catch(error => {
          const errorMessage = error?.message || 'Failed to process image';
          setValidationError(errorMessage);
          Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
            'Error message': errorMessage,
          });
          onDisabled(true);
        });
    };

    const validationImage = (imageFile?: File) => {
      const fileSize = getFileSizeMB(imageFile);

      if (!imageFile) return false;

      const allowedFormats = getAllowedFormats();
      const regExpAllowedFormats = new RegExp(`(${allowedFormats.regExpFormats})`, 'i');
      const imageName = imageFile?.name || '';

      if (!regExpAllowedFormats.test(imageName)) {
        const errorMessage = t(allowedFormats.errorMessage);
        setValidationError(errorMessage);
        Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
          'Error message': errorMessage,
        });
        onDisabled(true);
        return false;
      }

      if (!fileSize || fileSize > IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB) {
        const errorMessage = t('max_file_size_is_number_mb', {
          number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
        });
        setValidationError(errorMessage);
        Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
          'Error message': errorMessage,
        });
        onDisabled(true);
        return false;
      }

      setValidationError(null);
      onDisabled(false);
      return true;
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const imageFile = e?.currentTarget?.files?.[0];
      const isSuccessfullyValidated = validationImage(imageFile);
      if (isSuccessfullyValidated) handleImage(imageFile);
    };

    const renderContent = () => {
      if (croppedImage) {
        return (
          <Photo
            src={croppedImage}
            alt="Cropped preview"
            className="photo-box__uploaded-photo upload-box__row"
          />
        );
      }

      if (image && isCropping) {
        return (
          <Suspense fallback={<CropperLoader />}>
            <ImageCropper image={image} onCropComplete={onCropComplete} />
          </Suspense>
        );
      }

      return (
        <StyledUploadPhoto
          ref={ref}
          error={validationError}
          onChange={handleUpload}
          helperText={t<string>('accepted_file_formats_and_max_size', {
            number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
          })}
        />
      );
    };

    return (
      <>
        {renderContent()}
        <IncludedRow>
          <img src={notIncludedIcon} alt="Not included" width={24} height={24} />
          <p>{t('there_should_not_be_reflections')}</p>
        </IncludedRow>
        <IncludedRow>
          <img src={checkIcon} alt="Included" width={24} height={24} />
          <p>{t('use_clear_not_blurred_image')}</p>
        </IncludedRow>
      </>
    );
  },
);

export {UploadPhoto};
